// @file Composable that handles the progress of multiple draft publish attempts
import { n__, __ } from '@@/bits/intl'
import { SnackbarLoadingStatus, SnackbarNotificationType } from '@@/enums'
import type { GlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import type { DraftPost } from '@@/types'
import type { Ref } from 'vue'
import { ref, watch } from 'vue'

interface PublishAttempt {
  success: boolean
  isDone: boolean
  unwatch: () => void
}

export function useSnackbarPublishProgress(draftByCid: Ref<Record<string, DraftPost>>): {
  startTrackingStatus: (cid: string) => void
  markStatusAsPublished: (cid: string) => void
  markStatusAsFailed: (cid: string) => void
  stopTrackingStatus: (cid: string) => void
  resetStatus: () => void
} {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const publishingSnackbarUid = ref<string | null>(null)
  const publishAttempts = ref<Record<string, PublishAttempt>>({})

  // Snackbar messages
  const publishingDraftsSnackbarMessage = __('Publishing drafts...')

  // Watch the snackbar array and update the publishingSnackbarUid to null if it is not in the array,
  // since snackbar can also be dismissed by the user, or removed once the timeout is over
  watch(
    () => ({
      snackbars: globalSnackbarStore.snackbarArray,
      length: globalSnackbarStore.snackbarArray.length,
    }),
    ({ snackbars }) => {
      if (snackbars.every((snackbar) => snackbar.uid !== publishingSnackbarUid.value)) {
        publishingSnackbarUid.value = null
      }
    },
  )

  // Watch publish attempts and update snackbar accordingly
  watch(
    publishAttempts,
    (attempts) => {
      if (attempts == null || Object.keys(attempts).length === 0) {
        // Remove the snackbar if there are no attempts
        if (
          publishingSnackbarUid.value != null &&
          globalSnackbarStore.snackbarArray.find((snackbar) => snackbar.uid === publishingSnackbarUid.value)
            ?.message === publishingDraftsSnackbarMessage
        ) {
          globalSnackbarStore.removeSnackbar(publishingSnackbarUid.value)
        }
        return
      }

      const allDone = Object.values(attempts).every((attempt) => attempt.isDone)
      const totalCount = Object.keys(attempts).length
      const completedCount = Object.values(attempts).filter((attempt) => attempt.isDone).length
      const progress = totalCount > 0 ? completedCount / totalCount : 0

      if (!allDone) {
        // Still publishing - show or update progress snackbar
        const currentSnackbar = globalSnackbarStore.snackbarArray.find(
          (snackbar) => snackbar.uid === publishingSnackbarUid.value,
        )

        if (publishingSnackbarUid.value != null && currentSnackbar != null) {
          // Update existing snackbar progress
          if (currentSnackbar != null) {
            globalSnackbarStore.updateSnackbar(publishingSnackbarUid.value, {
              ...currentSnackbar,
              loadingState: {
                status: SnackbarLoadingStatus.loading,
                progress,
              },
            })
          }
        } else {
          // Create new snackbar
          publishingSnackbarUid.value = globalSnackbarStore.setSnackbar({
            message: publishingDraftsSnackbarMessage,
            notificationType: SnackbarNotificationType.spinner,
            loadingState: {
              status: SnackbarLoadingStatus.loading,
              progress,
            },
            persist: true,
          })
        }
        return
      }

      // All attempts are done - show final status
      const failedAttemptsCids = Object.keys(attempts).filter((cid) => !attempts[cid].success)

      const failedCount = failedAttemptsCids.length
      const successCount = totalCount - failedCount

      const successSnackbar: GlobalSnackbarNotification = {
        message: n__('%{num} draft has been published', '%{num} drafts have been published', successCount, {
          num: successCount,
        }),
        notificationType: SnackbarNotificationType.spinner,
        loadingState: {
          progress: 1,
          status: SnackbarLoadingStatus.success,
        },
        // We need to explicitly set actionText to undefined to remove it
        actionText: undefined,
        actionTextActions: undefined,
      }

      const failedSnackbar: GlobalSnackbarNotification = {
        message: n__('%{num} post could not be published', '%{num} posts could not be published', failedCount, {
          num: failedCount,
        }),
        notificationType: SnackbarNotificationType.error,
        actionText: __('Retry'),
        actionTextActions: [
          async () => await useSurfaceDraftsStore().publishSelectedDrafts(failedAttemptsCids),
          (_?: Event, uid?: string) => {
            if (uid != null) globalSnackbarStore.removeSnackbar(uid)
          },
        ],
        persist: true,
      }

      if (successCount > 0) {
        if (
          publishingSnackbarUid.value != null &&
          globalSnackbarStore.snackbarArray.some((snackbar) => snackbar.uid === publishingSnackbarUid.value)
        ) {
          globalSnackbarStore.updateSnackbar(publishingSnackbarUid.value, successSnackbar)
        } else {
          globalSnackbarStore.setSnackbar(successSnackbar)
        }
      }

      if (failedCount > 0) {
        if (successCount === 0 && publishingSnackbarUid.value != null) {
          globalSnackbarStore.removeSnackbar(publishingSnackbarUid.value)
        }
        globalSnackbarStore.setSnackbar(failedSnackbar)
      }

      // Clean up all tracking state
      resetStatus()
    },
    { deep: true },
  )

  const startTrackingStatus = (cid: string): void => {
    if (publishAttempts.value[cid] == null) {
      // Register a watcher for each draft
      const unwatch = watch(
        () => draftByCid.value[cid],
        (draft) => {
          // Ignore if the draft updated is not the one we are tracking
          if (publishAttempts.value[cid] == null) return

          // publishAttempts contains only the drafts which are being published
          // If a draft is deleted, or have auto-publish set to false during the upload,
          // it should be removed from publishAttempts
          if ((draft == null || draft.shouldAutoPublish === false) && !publishAttempts.value[cid].isDone) {
            stopTrackingStatus(cid)
            return
          }

          const uploadSuccess =
            // If draft is null (already published), consider it a success
            draft == null ||
            // Otherwise, check if upload is complete and successful
            (draft.uploadProgress === 1 && draft.uploadingFile == null && draft.uploadError == null)

          publishAttempts.value = {
            ...publishAttempts.value,
            [cid]: {
              ...publishAttempts.value[cid],
              success: uploadSuccess,
              isDone: draft == null || draft.uploadingFile == null,
            },
          }
        },
        { flush: 'sync' },
      )

      publishAttempts.value = {
        ...publishAttempts.value,
        [cid]: {
          success: false,
          isDone: false,
          unwatch,
        },
      }
    }

    // Create a new snackbar if there is not one already
    if (publishingSnackbarUid.value == null) {
      publishingSnackbarUid.value = globalSnackbarStore.setSnackbar({
        message: publishingDraftsSnackbarMessage,
        notificationType: SnackbarNotificationType.spinner,
        actionText: __('Stop'),
        actionTextActions: [
          useSurfaceDraftsStore().stopAutoPublishAllDrafts,
          (_?: Event, uid?: string) => {
            if (uid != null) globalSnackbarStore.removeSnackbar(uid)
          },
        ],
        persist: true,
      })
    }
  }

  const markStatusAsPublished = (cid: string): void => {
    if (publishAttempts.value[cid] != null) {
      publishAttempts.value = {
        ...publishAttempts.value,
        [cid]: { ...publishAttempts.value[cid], success: true, isDone: true },
      }
    }
  }

  const markStatusAsFailed = (cid: string): void => {
    if (publishAttempts.value[cid] != null) {
      publishAttempts.value = {
        ...publishAttempts.value,
        [cid]: { ...publishAttempts.value[cid], success: false, isDone: true },
      }
    }
  }

  const stopTrackingStatus = (cid: string): void => {
    if (publishAttempts.value[cid] != null) {
      const { [cid]: _, ...rest } = publishAttempts.value
      publishAttempts.value = rest
    }
  }

  const resetStatus = (): void => {
    Object.values(publishAttempts.value).forEach((attempt) => attempt.unwatch())
    publishAttempts.value = {}
  }

  return {
    startTrackingStatus,
    markStatusAsPublished,
    markStatusAsFailed,
    stopTrackingStatus,
    resetStatus,
  }
}
